import { makeAddNewCallback } from "../../redux/slices/addNew.ts";
import {
  CompanyCreate,
  DriverCreate,
  ProductCreate,
  VehicleCreate,
} from "../../api";
import { configProps } from "../../types";
import { TransportOrders } from "../../types/transportOrders.ts";
import DeliveryStatusTag from "../../components/DeliveryStatusTag.tsx";
import WaybillButton from "../../components/WaybillButton.tsx";
import { getCountryName } from "../../helpers/countryNames.ts";

export const transportOrdersConfig: configProps<"transportOrders"> = {
  view: "transportOrders",
  title: "Zlecenia transportowe",
  allowMultipleNewEntries: true,
  columns: [
    {
      id: 1,
      field: "unloadingDate",
      header: "Rozł",
      formGroup: "destination",
      formGroupIndex: 1,
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "calendar",
      isDefaultSort: true,
      sortOrder: -1 as const,
    },
    {
      id: 2,
      field: "loadingDate",
      header: "Zał",
      formGroup: "origin",
      formGroupIndex: 1,
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "calendar",
    },
    {
      id: 3,
      field: "weightAtUnloading",
      header: "Waga rozł.",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "number",
    },
    {
      id: 4,
      field: "weightAtLoading",
      header: "Waga zał.",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "number",
    },
    {
      id: 5,
      field: "deliveryStatus",
      header: "Status dostawy",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "deliveryStatus",
      editable: false,
      enableFilter: true,
      body: (rowData: TransportOrders) => (
        <div className="status-tag-wrapper">
          <DeliveryStatusTag deliveryStatus={rowData.deliveryStatus} />
          <WaybillButton
            deliveryStatus={rowData.deliveryStatus}
            rowId={rowData.id}
          />
        </div>
      ),
    },
    {
      id: 6,
      field: "product",
      header: "Towar",
      formGroup: "origin",
      formGroupIndex: 3,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "products",
      allowAddNew: makeAddNewCallback({
        field: "product",
        type: "product",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
          }) satisfies Partial<ProductCreate>,
      }),
      enableFilter: true,
    },
    {
      id: 7,
      field: "driver",
      header: "Kierowca",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--flexible",
      inputType: "dropdown",
      optionsKey: "drivers",
      allowAddNew: makeAddNewCallback({
        field: "driver",
        type: "driver",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            last_name: name,
          }) satisfies Partial<DriverCreate>,
      }),
      enableFilter: true,
    },
    {
      id: 8,
      field: "driverManager",
      header: "Kierownik",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--flexible",
      editable: false,
      optionsKey: "organizationalUsers",
      enableFilter: true,
      body: (rowData) => rowData.driverManager ?? "",
    },
    {
      id: 9,
      field: "trucks",
      header: "Ciezarowki",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trucks",
      allowAddNew: makeAddNewCallback({
        field: "trucks",
        type: "vehicle",
        makeInitialValues: ({
          initialValueString: number,
          row,
        }: {
          initialValueString: string;
          row: { driverId: number | null };
        }) =>
          ({
            number,
            type: "truck",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 10,
      field: "trailers",
      header: "Przyczepy",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trailers",
      allowAddNew: makeAddNewCallback({
        field: "trailers",
        type: "vehicle",
        makeInitialValues: ({
          initialValueString: number,
          row,
        }: {
          initialValueString: string;
          row: { driverId: number | null };
        }) =>
          ({
            number,
            type: "trailer",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 11,
      field: "seller",
      header: "Zakup",
      formGroup: "origin",
      formGroupIndex: 4,
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "suppliers",
      enableFilter: true,
      allowAddNew: makeAddNewCallback({
        field: "seller",
        type: "company",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
            is_buyer: false,
            is_transporter: false,
            is_supplier: true,
          }) satisfies Partial<CompanyCreate>,
      }),
    },
    {
      id: 12,
      field: "loadingLocation",
      header: "Załadunek",
      formGroup: "origin",
      formGroupIndex: 5,
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "locations",
      allowAddNew: makeAddNewCallback({
        field: "loadingLocation",
        type: "address",
        makeInitialValues: ({ initialValueString: address }) => ({ address }),
      }),
    },
    {
      id: 13,
      field: "loadingCity",
      header: "Miasto zał.",
      formGroup: "origin",
      formGroupIndex: 6,
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      optionsKey: "cities",
      enableFilter: true,
    },
    {
      id: 14,
      field: "loadingCountry",
      header: "Kraj zał.",
      formGroup: "origin",
      formGroupIndex: 7,
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      body: (rowData) => getCountryName(rowData.loadingCountry),
    },
    {
      id: 15,
      field: "purchasePrice",
      header: "Cena (zakup)",
      formGroup: "origin",
      formGroupIndex: 8,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "number",
    },
    {
      id: 16,
      field: "purchaseInvoice",
      header: "Faktura zak.",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
    },
    {
      id: 17,
      field: "purchaseDeductions",
      header: "Potrac (zakup)",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--large",
      inputType: "text",
    },
    {
      id: 18,
      field: "purchaseContract",
      header: "Kontrakt",
      formGroup: "origin",
      formGroupIndex: 2,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "purchaseContracts",
    },
    {
      id: 19,
      field: "buyer",
      header: "Sprzedaz",
      formGroup: "destination",
      formGroupIndex: 3,
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "buyers",
      enableFilter: true,
      required: (row) => !row.salesContract,
      allowAddNew: makeAddNewCallback({
        field: "buyer",
        type: "company",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
            is_buyer: true,
            is_transporter: false,
            is_supplier: false,
          }) satisfies Partial<CompanyCreate>,
      }),
    },
    {
      id: 20,
      field: "unloadingLocation",
      header: "Rozładunek",
      formGroup: "destination",
      formGroupIndex: 4,
      isDefaultVisible: false,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "locations",
      required: (row) => !row.salesContract,
      allowAddNew: makeAddNewCallback({
        field: "unloadingLocation",
        type: "address",
        makeInitialValues: ({ initialValueString: address }) => ({ address }),
      }),
    },
    {
      id: 21,
      field: "unloadingCity",
      header: "Miasto rozł.",
      formGroup: "destination",
      formGroupIndex: 5,
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      optionsKey: "cities",
      enableFilter: true,
    },
    {
      id: 22,
      field: "unloadingCountry",
      header: "Kraj rozł.",
      formGroup: "destination",
      formGroupIndex: 6,
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      body: (rowData) => getCountryName(rowData.unloadingCountry),
    },
    {
      id: 23,
      field: "salesPrice",
      header: "Cena (sprzedaz)",
      formGroup: "destination",
      formGroupIndex: 7,
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "number",
    },
    {
      id: 24,
      field: "salesInvoice",
      header: "Faktura (sprzedaz)",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
    },
    {
      id: 25,
      field: "salesDeduction",
      header: "Potrac (sprzedaz)",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
    },
    {
      id: 26,
      field: "salesContract",
      header: "Kontrakt (sprzedaz)",
      formGroup: "destination",
      formGroupIndex: 2,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "salesContracts",
    },
    {
      id: 27,
      field: "carrier",
      header: "Przewoznik",
      formGroup: "delivery",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "transporters",
      enableFilter: true,
      allowAddNew: makeAddNewCallback({
        field: "carrier",
        type: "company",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
            is_buyer: false,
            is_transporter: true,
            is_supplier: false,
          }) satisfies Partial<CompanyCreate>,
      }),
    },
    {
      id: 28,
      field: "transportRate",
      header: "Stawka Tr.",
      formGroup: "delivery",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "number",
    },
    {
      id: 29,
      field: "number",
      header: "Numer",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "text",
      editable: false,
    },
  ],
  fieldNameMap: {
    status: "deliveryStatus",
    unloading_date: "unloadingDate",
    loading_date: "loadingDate",
    loaded_weight: "weightAtLoading",
    unloaded_weight: "weightAtUnloading",
    driver_id: "driver",
    truck_id: "trucks",
    trailer_id: "trailers",
    product_id: "product",
    supplier_id: "seller",
    loading_location_id: "loadingLocation",
    purchase_price: "purchasePrice",
    purchase_invoice: "purchaseInvoice",
    purchase_deductions: "purchaseDeductions",
    purchase_contract_number: "purchaseContract",
    buyer_id: "buyer",
    unloading_location_id: "unloadingLocation",
    sales_price: "salesPrice",
    sales_invoice: "salesInvoice",
    sales_deductions: "salesDeduction",
    sales_contract_number: "salesContract",
    transporter_id: "carrier",
    transport_rate: "transportRate",
    marked_fields: "marked",
  },
};
